import { getUser, setUser } from "$lib/userstorage/usersotrage";
import { loadStorageEstablecimiento } from "$lib/java/establecimientos/establecimientostorage";

// Misma resolución de base URL que el resto de los *back.js.
let ruta_java = import.meta.env.VITE_RUTA_JAVA_SERVER;
let ruta_local_java = import.meta.env.VITE_RUTA_LOCAL_JAVA_SERVER;
let bd_local = import.meta.env.VITE_LOCAL_BD == "si";
let RUTA_JAVA = bd_local ? ruta_local_java : ruta_java;

const DEVICE_ID_KEY = "deviceId";

/**
 * UUID de instalación, persistente en localStorage. Identifica el
 * "dispositivo" para el refresh token (un refresh activo por user+device).
 * Mismo patrón que el clientUuid del outbox.
 */
export function getOrCreateDeviceId() {
    if (typeof localStorage === "undefined") return null;
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
}

// Single-flight: si ya hay un refresh en vuelo, todas las requests que dieron
// 401 comparten ese mismo Promise (un solo canje, no N).
let pendingRefresh = null;

/**
 * Canjea el refresh token por un nuevo access y lo guarda. Single-flight.
 * Devuelve:
 *   "ok"      -> access renovado y persistido
 *   "invalid" -> refresh vencido/revocado, o ya no pertenecés al establecimiento
 *   "network" -> sin red / server caído (NO es sesión muerta)
 */
export function refreshAccessToken() {
    if (pendingRefresh) return pendingRefresh;
    pendingRefresh = doRefresh().finally(() => {
        pendingRefresh = null;
    });
    return pendingRefresh;
}

async function doRefresh() {
    const user = getUser();
    const refreshToken = user?.refreshToken;
    if (!refreshToken) return "invalid";

    const cab = loadStorageEstablecimiento();
    const establishmentId = cab?.id || null;

    let res;
    try {
        res = await fetch(`${RUTA_JAVA}auth/refresh`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                refreshToken,
                establishmentId,
                deviceId: getOrCreateDeviceId(),
            }),
        });
    } catch (err) {
        return "network"; // fetch rechaza = sin red / server inalcanzable
    }

    if (res.ok) {
        let data;
        try {
            data = await res.json();
        } catch (e) {
            return "network";
        }
        if (!data?.token) return "invalid";
        setUser({ ...getUser(), token: data.token });
        return "ok";
    }

    // 5xx: el server está, pero falló transitoriamente -> reintentar luego.
    if (res.status >= 500) return "network";
    // 401 (refresh vencido/revocado) o 404 (te sacaron del establecimiento):
    // re-login. El manejo fino del 404 multi-est es de una fase posterior.
    return "invalid";
}

/**
 * Canjea el refresh token por un access scoped a `estId`, SIN tocar la sesión
 * activa (no hace setUser). Lo usa el drainer (drenar la cola de cada
 * establecimiento con su token) y el pre-cacheo eager (bajar el snapshot de
 * establecimientos que no son el activo). Multi-establecimiento.
 *
 * Devuelve { token, reason } con reason:
 *   "ok"       -> token scoped listo
 *   "network"  -> sin red / server caído / 5xx (reintentar)
 *   "removed"  -> 404: ya no pertenecés a ese establecimiento (te sacaron)
 *   "invalid"  -> 401 (refresh vencido/revocado) u otro
 *
 * `refreshTokenOverride` sirve para validar un refresh que todavía NO es el de
 * la sesión activa: lo usa "Continuar como [email]" del login para probar el
 * token guardado antes de dejar entrar. Sin él, se usa el de la sesión.
 */
export async function getScopedToken(estId, refreshTokenOverride = null) {
    const refreshToken = refreshTokenOverride ?? getUser()?.refreshToken;
    if (!refreshToken || !estId) return { token: null, reason: "invalid" };

    let res;
    try {
        res = await fetch(`${RUTA_JAVA}auth/refresh`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                refreshToken,
                establishmentId: estId,
                deviceId: getOrCreateDeviceId(),
            }),
        });
    } catch (err) {
        return { token: null, reason: "network" }; // sin red / server inalcanzable
    }
    if (res.ok) {
        try {
            const data = await res.json();
            return data?.token
                ? { token: data.token, reason: "ok" }
                : { token: null, reason: "invalid" };
        } catch (e) {
            return { token: null, reason: "network" };
        }
    }
    if (res.status === 404) return { token: null, reason: "removed" }; // te sacaron del est
    if (res.status >= 500) return { token: null, reason: "network" };
    return { token: null, reason: "invalid" }; // 401 u otro
}
